import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import {
  Activity, Heart, Thermometer, Wind, Droplets, ArrowLeft,
  Plus, Loader2, AlertTriangle, FileText, Droplet, ClipboardList,
} from 'lucide-react';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

const VITAL_FIELDS = [
  { key: 'bp_systolic',    label: 'BP Systolic',  color: '#ef4444', unit: 'mmHg',  normalMin: 90,  normalMax: 140 },
  { key: 'bp_diastolic',   label: 'BP Diastolic', color: '#f97316', unit: 'mmHg',  normalMin: 60,  normalMax: 90 },
  { key: 'pulse',          label: 'Pulse',        color: '#ec4899', unit: 'bpm',   normalMin: 60,  normalMax: 100 },
  { key: 'temp_c',         label: 'Temp (°C)',    color: '#eab308', unit: '°C',    normalMin: 36.1,normalMax: 37.2 },
  { key: 'spo2',           label: 'SpO₂',         color: '#3b82f6', unit: '%',     normalMin: 95,  normalMax: 100 },
  { key: 'rr',             label: 'Resp. Rate',   color: '#8b5cf6', unit: '/min',  normalMin: 12,  normalMax: 20 },
] as const;

const CHART_COLORS: Record<string, string> = {
  bp_systolic: '#ef4444',
  bp_diastolic:'#f97316',
  pulse:       '#ec4899',
  temp_c:      '#eab308',
  spo2:        '#3b82f6',
  rr:          '#8b5cf6',
};

function VitalBadge({ field, value, unit }: any) {
  const cfg = VITAL_FIELDS.find(f => f.key === field);
  if (!cfg || value == null) return null;
  const isAbnormal = value < cfg.normalMin || value > cfg.normalMax;
  return (
    <div className={clsx(
      'rounded-xl border p-3 text-center',
      isAbnormal ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'
    )}>
      <div className={clsx('text-xl font-bold', isAbnormal ? 'text-red-600' : 'text-navy')}>
        {typeof value === 'number' ? value.toFixed(field === 'temp_c' ? 1 : 0) : value}
        <span className="text-xs font-normal ml-1 text-gray-400">{unit}</span>
      </div>
      <div className="text-xs text-gray-500 mt-0.5 flex items-center justify-center gap-1">
        {isAbnormal && <AlertTriangle size={10} className="text-red-400" />}
        {cfg.label}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-3 text-xs min-w-[160px]">
      <p className="font-semibold text-navy mb-2">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-3 mb-1">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="font-semibold" style={{ color: p.color }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

const VitalEntryForm = ({ admissionId, onSuccess }: { admissionId: string; onSuccess: () => void }) => {
  const [form, setForm] = useState({
    recorded_at: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    bp_systolic: '', bp_diastolic: '', pulse: '', temp_c: '', spo2: '', rr: '',
    urine_output_ml: '', notes: '',
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) => api.post(`/ipd/admissions/${admissionId}/vitals`, data),
    onSuccess,
  });

  function field(k: string) {
    return {
      value: (form as any)[k],
      onChange: (e: any) => setForm(f => ({ ...f, [k]: e.target.value })),
    };
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5">
      <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
        <Plus size={15} className="text-orange" /> Record Vitals
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="col-span-2">
          <label className="text-xs font-semibold text-gray-600 block mb-1">Date & Time</label>
          <input type="datetime-local" {...field('recorded_at')}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
        </div>
        {[
          { key: 'bp_systolic', label: 'BP Systolic (mmHg)', placeholder: '120' },
          { key: 'bp_diastolic', label: 'BP Diastolic (mmHg)', placeholder: '80' },
          { key: 'pulse', label: 'Pulse (bpm)', placeholder: '72' },
          { key: 'temp_c', label: 'Temp (°C)', placeholder: '37.0' },
          { key: 'spo2', label: 'SpO₂ (%)', placeholder: '98' },
          { key: 'rr', label: 'Resp. Rate (/min)', placeholder: '16' },
          { key: 'urine_output_ml', label: 'Urine Output (ml)', placeholder: '' },
        ].map(f => (
          <div key={f.key}>
            <label className="text-xs font-semibold text-gray-600 block mb-1">{f.label}</label>
            <input type="number" placeholder={f.placeholder} {...field(f.key)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
          </div>
        ))}
        <div className="col-span-full">
          <label className="text-xs font-semibold text-gray-600 block mb-1">Notes</label>
          <input type="text" placeholder="Clinical notes..." {...field('notes')}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
        </div>
      </div>
      <button
        onClick={() => {
          const data: any = { recorded_at: form.recorded_at };
          if (form.bp_systolic)  data.bp_systolic     = Number(form.bp_systolic);
          if (form.bp_diastolic) data.bp_diastolic    = Number(form.bp_diastolic);
          if (form.pulse)        data.pulse            = Number(form.pulse);
          if (form.temp_c)       data.temp_c           = Number(form.temp_c);
          if (form.spo2)         data.spo2             = Number(form.spo2);
          if (form.rr)           data.rr               = Number(form.rr);
          if (form.urine_output_ml) data.urine_output_ml = Number(form.urine_output_ml);
          if (form.notes)        data.notes            = form.notes;
          saveMutation.mutate(data);
        }}
        disabled={saveMutation.isPending}
        className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-orange/90 disabled:opacity-40 transition">
        {saveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
        Save Vitals
      </button>
    </div>
  );
};

async function printDischargeSummary(admissionId: string) {
  const res = await api.get(`/ipd/admissions/${admissionId}/discharge-summary/html`, {
    responseType: 'text',
  });
  const w = window.open('', '_blank', 'width=900,height=1050');
  if (!w) return;
  w.document.write(res.data as string);
  w.document.close();
}

// ── Fluid Balance Tab ─────────────────────────────────────────────
function FluidBalanceTab({ admissionId }: { admissionId: string }) {
  const qc = useQueryClient();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate] = useState(today);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'intake', fluid_type: '', volume_ml: '', route: '', notes: '' });

  const { data: entries = [] } = useQuery({
    queryKey: ['fluid-balance', admissionId, date],
    queryFn: () => api.get(`/ipd/admissions/${admissionId}/fluid-balance`, { params: { date } }).then(r => r.data),
    refetchInterval: 60_000,
  });

  const addMutation = useMutation({
    mutationFn: (data: any) => api.post(`/ipd/admissions/${admissionId}/fluid-balance`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['fluid-balance', admissionId, date] }); setShowForm(false); setForm({ type: 'intake', fluid_type: '', volume_ml: '', route: '', notes: '' }); },
  });

  const inputTotal  = (entries as any[]).filter((e: any) => e.record_type === 'intake').reduce((s: number, e: any) => s + (e.volume_ml ?? 0), 0);
  const outputTotal = (entries as any[]).filter((e: any) => e.record_type === 'output').reduce((s: number, e: any) => s + (e.volume_ml ?? 0), 0);
  const balance     = inputTotal - outputTotal;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy" />
        <button onClick={() => setShowForm(s => !s)}
          className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-orange/90">
          <Plus size={14} /> Add Entry
        </button>
        <div className="ml-auto flex gap-4 text-sm">
          <span className="text-green-700 font-semibold">In: {inputTotal} ml</span>
          <span className="text-red-600 font-semibold">Out: {outputTotal} ml</span>
          <span className={clsx('font-bold', balance >= 0 ? 'text-blue-700' : 'text-red-700')}>
            Balance: {balance >= 0 ? '+' : ''}{balance} ml
          </span>
        </div>
      </div>

      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Type</label>
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy">
              <option value="intake">Intake (IV/Oral)</option>
              <option value="output">Output (Urine/Drain)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Fluid / Type</label>
            <input value={form.fluid_type} onChange={e => setForm(f => ({ ...f, fluid_type: e.target.value }))}
              placeholder="NS, RL, Urine..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Volume (ml)</label>
            <input type="number" value={form.volume_ml} onChange={e => setForm(f => ({ ...f, volume_ml: e.target.value }))}
              placeholder="500"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Route</label>
            <input value={form.route} onChange={e => setForm(f => ({ ...f, route: e.target.value }))}
              placeholder="IV, Oral, NG..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Notes</label>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Optional notes"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy" />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => addMutation.mutate({ ...form, volume_ml: parseFloat(form.volume_ml) || 0 })}
              disabled={addMutation.isPending || !form.volume_ml}
              className="w-full flex items-center justify-center gap-2 bg-navy text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-navy/90 disabled:opacity-40">
              {addMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Save
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {(entries as any[]).length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Droplet size={28} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No fluid balance entries for {date}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Time</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500">Type</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500">Fluid</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500">Route</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Volume (ml)</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">By</th>
              </tr>
            </thead>
            <tbody>
              {(entries as any[]).map((e: any) => (
                <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50/40">
                  <td className="px-4 py-2.5 text-gray-500 text-xs">
                    {format(new Date(e.recorded_at), 'HH:mm')}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',
                      e.record_type === 'intake' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600')}>
                      {e.record_type === 'intake' ? 'IN' : 'OUT'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-700">{e.fluid_type ?? '—'}</td>
                  <td className="px-4 py-2.5 text-gray-500">{e.route ?? '—'}</td>
                  <td className={clsx('px-4 py-2.5 text-right font-semibold',
                    e.record_type === 'intake' ? 'text-green-700' : 'text-red-600')}>
                    {e.record_type === 'intake' ? '+' : '-'}{e.volume_ml}
                  </td>
                  <td className="px-4 py-2.5 text-gray-400 text-xs">{e.recorded_by_name ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Nursing Orders Tab ────────────────────────────────────────────
function NursingOrdersTab({ admissionId }: { admissionId: string }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ order_type: 'medication', description: '' });

  const { data: orders = [] } = useQuery({
    queryKey: ['nursing-orders', admissionId],
    queryFn: () => api.get(`/ipd/admissions/${admissionId}/nursing-orders`).then(r => r.data),
  });

  const addMutation = useMutation({
    mutationFn: (data: any) => api.post(`/ipd/admissions/${admissionId}/nursing-orders`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['nursing-orders', admissionId] }); setShowForm(false); },
  });

  const completeMutation = useMutation({
    mutationFn: ({ id, status }: any) => api.patch(`/ipd/nursing-orders/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nursing-orders', admissionId] }),
  });

  const STATUS_STYLE: Record<string, string> = {
    pending:   'bg-yellow-100 text-yellow-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-500',
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(s => !s)}
          className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-orange/90">
          <Plus size={14} /> New Order
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Order Type</label>
            <select value={form.order_type} onChange={e => setForm(f => ({ ...f, order_type: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy">
              {['medication','dressing','positioning','monitoring','oxygen','blood_transfusion','other'].map(t => (
                <option key={t} value={t}>{t.replace('_', ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Description</label>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Order details..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy" />
          </div>
          <div className="col-span-2 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={() => addMutation.mutate(form)} disabled={addMutation.isPending || !form.description}
              className="flex items-center gap-2 bg-navy text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-navy/90 disabled:opacity-40">
              {addMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Save
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {(orders as any[]).length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <ClipboardList size={28} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No nursing orders yet</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Type</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Description</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500">Status</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500">Ordered By</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500">Date</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {(orders as any[]).map((o: any) => (
                <tr key={o.id} className="border-b border-gray-50 hover:bg-gray-50/40">
                  <td className="px-4 py-2.5 text-xs font-medium text-navy capitalize">
                    {o.order_type?.replace('_', ' ')}
                  </td>
                  <td className="px-4 py-2.5 text-gray-700">{o.description}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_STYLE[o.status] ?? 'bg-gray-100')}>
                      {o.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-400 text-xs">{o.ordered_by_name ?? '—'}</td>
                  <td className="px-4 py-2.5 text-gray-400 text-xs">
                    {o.order_date ? format(new Date(o.order_date), 'dd MMM HH:mm') : '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    {o.status === 'pending' && (
                      <div className="flex gap-1">
                        <button onClick={() => completeMutation.mutate({ id: o.id, status: 'completed' })}
                          className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs hover:bg-green-200">
                          Done
                        </button>
                        <button onClick={() => completeMutation.mutate({ id: o.id, status: 'cancelled' })}
                          className="px-2 py-1 bg-red-50 text-red-500 rounded-lg text-xs hover:bg-red-100">
                          Cancel
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function VitalsChartPage() {
  const { admissionId } = useParams<{ admissionId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showEntry, setShowEntry] = useState(false);
  const [activeTab, setActiveTab] = useState<'vitals' | 'fluid' | 'nursing'>('vitals');
  const [activeLines, setActiveLines] = useState<Set<string>>(new Set(['bp_systolic', 'bp_diastolic', 'pulse', 'spo2']));

  const { data: vitals, isLoading } = useQuery({
    queryKey: ['vitals', admissionId],
    queryFn: () => api.get(`/ipd/admissions/${admissionId}/vitals`).then(r => r.data),
    refetchInterval: 60_000,
  });

  const vitalList: any[] = vitals ?? [];

  const chartData = vitalList.map((v: any) => ({
    time: format(new Date(v.recorded_at), 'dd/MM HH:mm'),
    bp_systolic:  v.bp_systolic,
    bp_diastolic: v.bp_diastolic,
    pulse:        v.pulse,
    temp_c:       v.temp_c,
    spo2:         v.spo2,
    rr:           v.rr,
    urine_output_ml: v.urine_output_ml,
  })).reverse();

  const latest = vitalList[0];

  function toggleLine(key: string) {
    setActiveLines(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  const TABS = [
    { key: 'vitals',  label: 'Vitals Chart',    icon: Activity },
    { key: 'fluid',   label: 'Fluid Balance',   icon: Droplet },
    { key: 'nursing', label: 'Nursing Orders',  icon: ClipboardList },
  ] as const;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)}
          className="p-2 rounded-xl hover:bg-gray-100 transition text-gray-500">
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-navy flex items-center gap-2">
            <Activity size={18} className="text-orange" /> IPD Clinical Chart
          </h1>
          <p className="text-xs text-gray-400">Admission: {admissionId?.slice(0, 8)}…</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => printDischargeSummary(admissionId!)}
            className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
            <FileText size={14} /> Discharge Summary
          </button>
          {activeTab === 'vitals' && (
            <button onClick={() => setShowEntry(s => !s)}
              className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-orange/90 transition">
              <Plus size={14} /> Record Vitals
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition',
              activeTab === tab.key
                ? 'bg-white text-navy shadow-sm'
                : 'text-gray-500 hover:text-navy'
            )}
          >
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'fluid'   && <FluidBalanceTab   admissionId={admissionId!} />}
      {activeTab === 'nursing' && <NursingOrdersTab  admissionId={admissionId!} />}

      {activeTab === 'vitals' && showEntry && (
        <VitalEntryForm
          admissionId={admissionId!}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['vitals', admissionId] });
            setShowEntry(false);
          }}
        />
      )}

      {activeTab === 'vitals' && (
        <>
          {/* Latest vitals snapshot */}
          {latest && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {VITAL_FIELDS.map(f => (
                <VitalBadge key={f.key} field={f.key} value={latest[f.key]} unit={f.unit} />
              ))}
            </div>
          )}

          {/* Chart */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-semibold text-navy text-sm">Vitals Trend</h2>
              <div className="flex flex-wrap gap-2">
                {VITAL_FIELDS.map(f => (
                  <button
                    key={f.key}
                    onClick={() => toggleLine(f.key)}
                    className={clsx(
                      'flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-medium border transition',
                      activeLines.has(f.key)
                        ? 'border-transparent text-white'
                        : 'bg-white border-gray-200 text-gray-500'
                    )}
                    style={activeLines.has(f.key) ? { backgroundColor: f.color, borderColor: f.color } : {}}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 size={22} className="animate-spin text-orange" />
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <Activity size={32} className="mb-2 opacity-25" />
                <p className="text-sm font-medium text-navy">No vitals recorded yet</p>
                <button onClick={() => setShowEntry(true)}
                  className="mt-2 text-xs text-orange hover:underline flex items-center gap-1">
                  <Plus size={11} /> Record first vitals
                </button>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="time" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <Tooltip content={<CustomTooltip />} />
                  {VITAL_FIELDS.filter(f => activeLines.has(f.key)).map(f => (
                    <Line
                      key={f.key}
                      type="monotone"
                      dataKey={f.key}
                      name={f.label}
                      stroke={f.color}
                      strokeWidth={2}
                      dot={{ r: 3, fill: f.color }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Raw data table */}
          {vitalList.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-50">
                <h3 className="font-semibold text-navy text-sm">Vitals Log</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-50 bg-gray-50/50">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Time</th>
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">BP</th>
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">Pulse</th>
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">Temp</th>
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">SpO₂</th>
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">RR</th>
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">Urine</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vitalList.map((v: any) => {
                      const bpAbn = (v.bp_systolic && (v.bp_systolic > 140 || v.bp_systolic < 90)) ||
                                    (v.bp_diastolic && (v.bp_diastolic > 90 || v.bp_diastolic < 60));
                      const spo2Abn = v.spo2 && v.spo2 < 95;
                      return (
                        <tr key={v.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition text-xs">
                          <td className="px-4 py-2.5 text-gray-500">
                            {format(new Date(v.recorded_at), 'dd MMM HH:mm')}
                          </td>
                          <td className={clsx('px-3 py-2.5 text-center font-medium', bpAbn ? 'text-red-600' : 'text-gray-700')}>
                            {v.bp_systolic && v.bp_diastolic ? `${v.bp_systolic}/${v.bp_diastolic}` : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-center text-gray-700">{v.pulse ?? '—'}</td>
                          <td className={clsx('px-3 py-2.5 text-center font-medium',
                            v.temp_c && (v.temp_c > 37.5 || v.temp_c < 36) ? 'text-yellow-600' : 'text-gray-700')}>
                            {v.temp_c ? `${Number(v.temp_c).toFixed(1)}°` : '—'}
                          </td>
                          <td className={clsx('px-3 py-2.5 text-center font-medium', spo2Abn ? 'text-red-600' : 'text-gray-700')}>
                            {v.spo2 ? `${v.spo2}%` : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-center text-gray-700">{v.rr ?? '—'}</td>
                          <td className="px-3 py-2.5 text-center text-gray-700">{v.urine_output_ml ? `${v.urine_output_ml}ml` : '—'}</td>
                          <td className="px-3 py-2.5 text-gray-400">{v.recorded_by_name ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
